import { LuMessageCircle } from 'react-icons/lu'
import { CgMoreO } from 'react-icons/cg'
import { FaRegUserCircle } from 'react-icons/fa'

import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import { cn } from '#/lib/utils'
import { Link } from '@tanstack/react-router'
import { getImageUrl } from '#/lib/imageUrl'

type BlogCardProps = {
  id: string
  image: string
  title: string
  excerpt: string
  author: string
  comments: string
  readingTime?: number
  date: { day: string; month: string; year: string }
  tags?: string[]
  showTags?: boolean
  actionLabel?: string
  className?: string
}

export default function BlogCard({
  id,
  image,
  title,
  excerpt,
  author,
  comments,
  date,
  tags = [],
  showTags = true,
  actionLabel = 'Read More',
  className,
}: BlogCardProps) {
  return (
    <article
      className={cn(
        'flex w-full h-full flex-col items-start overflow-clip rounded-[12px] bg-white shadow-[0px_4px_20px_0px_rgba(106,155,145,0.16)]',
        className,
      )}
    >
      <div className="relative h-60 w-full">
        <img
          alt=""
          className="absolute inset-0 size-full object-cover"
          src={getImageUrl(image)}
          loading="lazy"
        />
        <div className="absolute left-6.25 top-6.25 flex flex-col items-center justify-center gap-[6px] rounded-[10px] bg-[color:var(--primary-500)] px-[14px] py-[6px] text-white">
          <p className="text-[32px] font-bold leading-9">{date.day}</p>
          <p className="text-[14px] font-medium leading-[normal]">
            {date.month} {date.year}
          </p>
        </div>
      </div>
      <div className="flex flex-1 w-full flex-col items-start gap-4 p-7.5">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-1">
            <LuMessageCircle className="text-[16px] text-(--neutral-500)" />
            <p className="text-[12px] font-normal text-(--neutral-500)">
              {comments}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <FaRegUserCircle className="text-[16px] text-(--neutral-500)" />
            <p className="text-[12px] font-normal text-(--neutral-500)">
              By: {author}
            </p>
          </div>
        </div>
        {showTags ? (
          <div className="flex w-full flex-wrap gap-2">
            {tags.map((tag) => (
              <Badge
                key={`${title}-${tag}`}
                variant="outline"
                className="    text-[11px] font-semibold capitalize tracking-[0.04em] "
              >
                {tag}
              </Badge>
            ))}
          </div>
        ) : null}
        <div className="flex w-full flex-col items-start gap-2">
          <h3 className="w-full text-[22px] font-bold capitalize leading-[normal] text-[color:var(--neutral-900)]">
            {title}
          </h3>
          <p className="w-full text-[16px] font-normal leading-[normal] text-[color:var(--neutral-500)]">
            {excerpt}
          </p>
        </div>
        <Link
          to="/blog/$blogId"
          params={{ blogId: id }}
          className="mt-auto"
          aria-label={`${actionLabel}: ${title}`}
        >
          <Button variant="outline">
            <CgMoreO className="text-[24px]" />
            {actionLabel}
            <span className="sr-only">: {title}</span>
          </Button>
        </Link>
      </div>
    </article>
  )
}
